import {
  BrowserCacheLocation,
  type Configuration,
  type RedirectRequest
} from '@azure/msal-browser';

/**
 * Environment-backed MSAL configuration for Entra Workforce.
 *
 * Platform injects `ENTRA_*`. Browser code also accepts `VITE_ENTRA_*` when present.
 * Redirect URI is always normalized to the project origin root (`/`) so it matches the
 * shared CDF app-registration wildcard (`https://*.dev.cdf…/`).
 */

function readPublicEnv(name: string): string | undefined {
  const meta = import.meta.env as Record<string, string | boolean | undefined>;
  const candidates = [`VITE_${name}`, name];
  for (const key of candidates) {
    const raw = meta[key];
    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      if (trimmed) return trimmed;
    }
  }
  return undefined;
}

/** True when Entra Workforce client configuration is present and enabled. */
export function isAuthConfigured(): boolean {
  const explicitlyEnabled = readPublicEnv('ENTRA_ENABLED');
  if (explicitlyEnabled === 'false') return false;

  const clientId = readPublicEnv('ENTRA_CLIENT_ID');
  const authority = readPublicEnv('ENTRA_AUTHORITY');
  if (!clientId || !authority) return false;

  // When ENTRA_ENABLED is set, require the truthy flag. When absent, client+authority are enough.
  if (explicitlyEnabled !== undefined && explicitlyEnabled !== 'true') return false;
  return true;
}

/**
 * Always the project origin root, including trailing `/`.
 * Configured ENTRA_REDIRECT_URI values that point at `/redirect.html` or callbacks are collapsed.
 */
export function getRedirectUri(): string {
  const configured = readPublicEnv('ENTRA_REDIRECT_URI');
  try {
    if (configured) {
      const url = new URL(configured, window.location.origin);
      return `${url.origin}/`;
    }
  } catch {
    // fall through to window origin
  }
  return `${window.location.origin}/`;
}

export function getPostLogoutRedirectUri(): string {
  const configured =
    readPublicEnv('ENTRA_POST_LOGIN_REDIRECT') ?? readPublicEnv('ENTRA_REDIRECT_URI');
  try {
    if (configured) {
      const url = new URL(configured, window.location.origin);
      // Only allow same-origin post-logout targets; always land on a path under `/`.
      if (url.origin === window.location.origin) {
        return `${url.origin}${url.pathname.startsWith('/') ? url.pathname : '/'}` || `${url.origin}/`;
      }
    }
  } catch {
    // fall through
  }
  return getRedirectUri();
}

/** OIDC / Graph scopes for the interactive browser session. */
export function getLoginScopes(): string[] {
  return (readPublicEnv('ENTRA_SCOPES') ?? 'openid profile email offline_access')
    .split(/\s+/)
    .filter(Boolean);
}

/**
 * Custom API delegated scope(s) for Fastify bearer tokens.
 * When unset, derives the scope from the client ID as
 * `api://<clientId>/access_as_user` (standard Entra Application ID URI).
 * When no client ID is configured either, no API bearer token is sent —
 * the API requires a delegated access token and rejects ID tokens.
 */
export function getApiScopes(): string[] | null {
  const raw = readPublicEnv('ENTRA_API_SCOPE');
  if (raw) {
    const scopes = raw.split(/\s+/).filter(Boolean);
    if (scopes.length > 0) return scopes;
  }
  // Derive from client ID: api://<clientId>/access_as_user
  const clientId = readPublicEnv('ENTRA_CLIENT_ID');
  const requiredScp = readPublicEnv('ENTRA_REQUIRED_SCP') ?? 'access_as_user';
  if (clientId) {
    return [`api://${clientId}/${requiredScp}`];
  }
  return null;
}

export function buildMsalConfig(): Configuration {
  const clientId = readPublicEnv('ENTRA_CLIENT_ID');
  const authority = readPublicEnv('ENTRA_AUTHORITY');
  if (!clientId || !authority) {
    throw new Error('MSAL requires ENTRA_CLIENT_ID and ENTRA_AUTHORITY.');
  }

  return {
    auth: {
      clientId,
      authority,
      redirectUri: getRedirectUri(),
      postLogoutRedirectUri: getPostLogoutRedirectUri()
    },
    cache: {
      // Shared across same-origin tabs (preview iframe + top-level sign-in tab).
      // localStorage is required so the top-level sign-in tab can hand off the
      // auth result to the embedded preview iframe. sessionStorage would break
      // this cross-tab handoff. The CSP (set by the API server) restricts
      // script-src to 'self' and trusted hashes, preventing injected scripts
      // from reading localStorage — providing defense-in-depth for the token
      // cache without sacrificing the iframe authentication flow.
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      allowRedirectInIframe: false,
      allowPlatformBroker: false
    }
  };
}

export function buildLoginRequest(redirectStartPage?: string): RedirectRequest {
  return {
    scopes: getLoginScopes(),
    redirectUri: getRedirectUri(),
    ...(redirectStartPage ? { redirectStartPage } : {})
  };
}

export function buildTokenRequest(
  account: RedirectRequest['account'],
  redirectStartPage?: string
): RedirectRequest {
  const apiScopes = getApiScopes();
  return {
    account: account ?? undefined,
    scopes: apiScopes ?? getLoginScopes(),
    redirectUri: getRedirectUri(),
    ...(redirectStartPage ? { redirectStartPage } : {})
  };
}
